import { Charts, Info, Market, Plus } from '@nexds/web'
import { useTheme } from 'styled-components'

import { ADHP, Waypoint } from '@/domain/models'
import { AddWaypointToRouteUseCase } from '@/domain/useCases/Route'

import { AddToRoute, City, Container, Header, Link, Options, WaypointName } from './WaypointInfo.styles'

import { MetricsEvents } from '@/constants/metricsEvents'
import { useI18n } from '@/presentation/hooks/useI18n'
import { getAdhpInfoPath } from '@/presentation/modules/AdhpInfo/utils/adhpInfoNavigation'
import { getProcedureChartsPath } from '@/presentation/modules/AdhpInfo/utils/procedureChartsNavigation'
import { openDrawer } from '@/presentation/router/DrawerRouter'
import { RouterPaths } from '@/presentation/router/RouterPathsMapper'
import { sendMetrics } from '@/presentation/utils/sendMetrics'

interface WaypointInfoProps {
  name: string
  waypoint: Waypoint | ADHP
  icao?: string
  city?: string
  uf?: string
}

export function WaypointInfo(props: WaypointInfoProps) {
  const { name, city, uf, icao, waypoint } = props

  const theme = useTheme()
  const { t } = useI18n()

  function handleAddToRoute() {
    const updatedRoute = new AddWaypointToRouteUseCase().execute(waypoint)
    if (updatedRoute.isFailure) return

    sendMetrics(MetricsEvents.MAP_ADHP_SELECTED, { extra: 'add-to-route' })

    // Espelha o fluxo da busca (MapSearchPresenter): ao adicionar o primeiro ponto,
    // abre o painel de rotas. Nos pontos seguintes o painel já está aberto.
    if (updatedRoute.getValue().waypoints.length === 1) {
      openDrawer(RouterPaths.ROUTES_PANEL)
    }
  }

  function handleOpenAdhpInfo() {
    if (!icao) return

    openDrawer(getAdhpInfoPath({ code: icao, tab: 'rotaer' }))
    sendMetrics(MetricsEvents.MAP_ADHP_SELECTED, { extra: 'info' })
  }

  function handleOpenProcedures() {
    if (!icao || !(waypoint instanceof ADHP)) return

    openDrawer(getProcedureChartsPath(icao))
    sendMetrics(MetricsEvents.MAP_ADHP_PROCEDURES_SELECTED, {
      extra: waypoint.type === 'AD' ? 'aerodrome' : 'helipad'
    })
  }

  function handleOpenMarket() {
    if (!icao || !(waypoint instanceof ADHP)) return

    const normalizedIcao = icao.trim().toUpperCase()
    if (!normalizedIcao) return

    openDrawer(`${RouterPaths.MARKET}?icao=${encodeURIComponent(normalizedIcao)}`)
    sendMetrics(MetricsEvents.MAP_ADHP_SERVICES_SELECTED, {
      extra: waypoint.type === 'AD' ? 'aerodrome' : 'helipad'
    })
  }

  return (
    <Container>
      <Header>
        <WaypointName>{name}</WaypointName>
        {city && uf && <City>{`${city}, ${uf}`}</City>}
      </Header>
      <Options>
        <AddToRoute onClick={handleAddToRoute}>
          <Plus color={theme.colors.secondary} size={'md'} />
          {t('MAP-POINTS_MODAL_ADD-TO-ROUTE')}
        </AddToRoute>
        {icao && waypoint instanceof ADHP && (
          <>
            <Link type="button" onClick={handleOpenAdhpInfo}>
              <Info color={theme.colors.secondary} size={'md'} />
              {waypoint.type === 'AD' ? t('MAP-POINTS_MODAL_INFO-AERODROME') : t('MAP-POINTS_MODAL_INFO-HELIPAD')}
            </Link>
            <Link type="button" onClick={handleOpenProcedures}>
              <Charts color={theme.colors.secondary} size={'md'} />
              {t('MAP-POINTS_MODAL_PROCEDURES')}
            </Link>
            <Link type="button" onClick={handleOpenMarket}>
              <Market color={theme.colors.secondary} size={'md'} />
              {t('MAP-POINTS_MODAL_VIEW-SERVICES')}
            </Link>
          </>
        )}
      </Options>
    </Container>
  )
}
